import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import dateFns from 'date-fns'
import VisibilitySensor from 'react-visibility-sensor'
import theme from 'styles/theme'
import { Container } from 'styles/sharedStyle'
import { Body10 } from 'components/Typography'
import trackGTM from 'utils/trackGTM'
import GA4 from 'utils/GA4'
import ReactPlayerLoader from '@brightcove/react-player-loader'
import Image from 'components/Image'
import { fixAudiences } from '../../utils/audiences'
import VideoPlayButton from './VideoPlayButton'
import {
  Wrapper,
  Overlay,
  PlaceholderImg,
  Divider,
  LazyLoadContainer,
} from './VideoPlayer.style'

const VideoPlayer = ({
  id,
  audiences,
  name,
  autoplay,
  inCarousel,
  carouselActive,
  controls,
  image,
  loop,
  muted,
  video,
  hasSpacing,
  videoMode,
  noLazyload,
  villageSlug,
  isStandalone,
}) => {
  const [player, setPlayer] = useState(null)
  const [isLoaded, setIsLoaded] = useState(false)
  const [showPlaceholder, setShowPlaceholder] = useState(true)
  const [isPlaying, setIsPlaying] = useState(autoplay)
  const [placeholderHidden, setPlaceholderHidden] = useState(false)
  const [isFinished, setIsFinished] = useState(false)
  const [hasError, setHasError] = useState(false)
  const [duration, setDuration] = useState(0)
  const [windowWidth, setWindowWidth] = useState(1200)
  const [inViewport, setInViewport] = useState(false)
  const [percentagesPlayed, setPercentagesPlayed] = useState({
    25: false,
    50: false,
    75: false,
    100: false,
  })
  const [percentagePlayed, setPercentagePlayed] = useState(0)
  const prevWindowWidthRef = useRef()
  const BREAKPOINT = 900
  const videoType =
    videoMode || (windowWidth > BREAKPOINT ? 'landscape' : 'portrait')
  const hasAudiences = !!audiences

  const formattedTime = (seconds) => {
    const helperDate = dateFns.addSeconds(new Date(0), seconds)
    return dateFns.format(helperDate, 'mm:ss')
  }

  const computePercentagePlayed = (currentTime) => {
    const total = player.duration()
    return (currentTime / total) * 100
  }

  useEffect(() => {
    if (percentagePlayed === 25 && !percentagesPlayed[25]) {
      trackGTM(
        'video engagement',
        'video engagement',
        `${percentagePlayed}%`,
        name
      )
      if (isStandalone) {
        GA4('video_progress', {
          video_title: name,
          video_progress: `${percentagePlayed}%`,
          village_name: villageSlug,
        })
      }
    }

    if (percentagePlayed === 50 && !percentagesPlayed[50]) {
      trackGTM(
        'video engagement',
        'video engagement',
        `${percentagePlayed}%`,
        name
      )
      if (isStandalone) {
        GA4('video_progress', {
          video_title: name,
          video_progress: `${percentagePlayed}%`,
          village_name: villageSlug,
        })
      }
    }

    if (percentagePlayed === 75 && !percentagesPlayed[75]) {
      trackGTM(
        'video engagement',
        'video engagement',
        `${percentagePlayed}%`,
        name
      )
      if (isStandalone) {
        GA4('video_progress', {
          video_title: name,
          video_progress: `${percentagePlayed}%`,
          village_name: villageSlug,
        })
      }
    }
    if (percentagePlayed === 100 && !percentagesPlayed[100]) {
      trackGTM(
        'video engagement',
        'video engagement',
        `${percentagePlayed}%`,
        name
      )
      if (isStandalone) {
        GA4('video_progress', {
          video_title: name,
          video_progress: 'complete',
          village_name: villageSlug,
        })
      }
    }
  }, [percentagePlayed])

  // Adds additional controls to controlbar
  const addControls = () => {
    if (controls) {
      const controlBar = player.controlBar.el()
      const fullScreenBtn = document.createElement('button')
      fullScreenBtn.classList.add(
        'vjs-fullscreen-control',
        'vjs-control',
        'vjs-button'
      )
      fullScreenBtn.onclick = () => {
        if (!player.isFullscreen()) {
          player.requestFullscreen()
        } else {
          player.exitFullscreen()
        }
      }
      fullScreenBtn.innerHTML = `<span class="vjs-icon-placeholder"></span>`
      controlBar.appendChild(fullScreenBtn)
    }
  }

  const autoplayMobile = () => {
    if (autoplay && !inCarousel) {
      player.children()[0].setAttribute('playsinline', 'playsinline')
      setPlaceholderHidden(true)
    }
  }

  const handleSuccess = (success) => {
    if (!player && !isLoaded) {
      setPlayer(success.ref)
      setIsLoaded(true)
    }
    setShowPlaceholder(false)
  }

  const handleError = () => {
    if (isLoaded) {
      setShowPlaceholder(true)
      setHasError(true)
      player.pause()
    }
  }

  const getVideo = (el) => {
    player.catalog.getVideo(el, (error, media) => {
      if (!error) {
        player.catalog.load(media)
      } else {
        handleError()
      }
    })
  }

  const handleFirstPlay = () => {
    player.on('canplaythrough', () => {
      if (carouselActive) {
        player.pause()
      }
      if (controls) {
        setDuration(player.duration())
      }
    })
  }

  const handleResize = () => {
    const prevWindowWidth = prevWindowWidthRef.current
    prevWindowWidthRef.current = window.innerWidth
    setWindowWidth(window.innerWidth)

    const prepareVideo = (type) => {
      if (!autoplay) setIsPlaying(false)
      setShowPlaceholder(true)
      getVideo(video[type])
      setDuration(player.duration())
    }

    if (prevWindowWidth < BREAKPOINT && window.innerWidth > BREAKPOINT) {
      prepareVideo('landscape')
    }
    if (prevWindowWidth > BREAKPOINT && window.innerWidth < BREAKPOINT) {
      prepareVideo('portrait')
    }
  }

  const handleTracking = () => {
    const trackingInterval = window.setInterval(() => {
      const currentPercentage = Math.round(
        computePercentagePlayed(player.currentTime())
      )

      if (player.paused()) {
        window.clearInterval(trackingInterval)
      }

      if (currentPercentage === 25) {
        setPercentagePlayed(currentPercentage)
        setPercentagesPlayed({ ...percentagesPlayed, 25: true })
      }
      if (currentPercentage === 50) {
        setPercentagePlayed(currentPercentage)
        setPercentagesPlayed({ ...percentagesPlayed, 50: true })
      }
      if (currentPercentage === 75) {
        setPercentagePlayed(currentPercentage)
        setPercentagesPlayed({ ...percentagesPlayed, 75: true })
      }
      if (currentPercentage === 100) {
        setPercentagePlayed(currentPercentage)
        setPercentagesPlayed({ ...percentagesPlayed, 100: true })
      }
    }, 0)
  }

  const handleTogglePlay = () => {
    if (isLoaded && player.paused()) {
      player.play()
      trackGTM('video engagement', 'autoplay', `video play`, name)
    } else {
      player.pause()
    }
    handleTracking()
  }

  const handleIsPlaying = () => {
    player.on('pause', () => {
      const currentPercentage = Math.round(
        computePercentagePlayed(player.currentTime())
      )
      if (controls && !player.scrubbing() && !player.isFullscreen()) {
        setIsPlaying(false)
        setPlaceholderHidden(false)
      }
      if (currentPercentage < 100) {
        trackGTM('video engagement', 'autoplay', 'video pause', name)
        if (isStandalone) {
          GA4('video_pause', { video_title: name, village_name: villageSlug })
        }
      }
    })

    player.on('play', () => {
      // Introduce counter stop in case interval method does not clear
      let counter = 0
      const placeholderInterval = setInterval(() => {
        counter += 1
        if (player.currentTime() > 0) {
          setPlaceholderHidden(true)
          clearInterval(placeholderInterval)
        }

        if (counter === 3) {
          clearInterval(placeholderInterval)
        }
      }, 1000)

      if (controls) {
        setIsPlaying(true)
        setIsFinished(false)
        setShowPlaceholder(false)
      }
      if (isStandalone) {
        GA4('video_start', { video_title: name, village_name: villageSlug })
      }
    })
  }

  const handleEnded = () => {
    player.on('ended', () => {
      setIsPlaying(false)
      setIsFinished(true)
      player.currentTime(0)
      setShowPlaceholder(true)
      setPlaceholderHidden(false)
    })
  }

  const watchErrors = () => {
    player.on('error', () => {
      player.errorDisplay.hide()
    })
  }

  const onChange = (isVisible) => {
    setInViewport(isVisible)
  }

  useEffect(() => {
    setWindowWidth(window.innerWidth)

    if (player && isLoaded) {
      window.addEventListener('resize', handleResize)
      getVideo(video[videoType])
      handleFirstPlay()
      handleIsPlaying()
      handleEnded()
      watchErrors()
      autoplayMobile()
      addControls()
      window.onbeforeunload = () => {
        setPlaceholderHidden(false)
      }
    }

    return () => {
      window.removeEventListener('resize', handleResize)
      if (player && isLoaded) {
        try {
          player.pause()
          // player still gets called after dispose:
          // https://github.com/videojs/video.js/issues/3755
        } catch {
          // case of the method of the method not being ready to be executed
          return
        }
        setTimeout(() => {
          player.dispose()
        }, 0)
        setPlayer(null)
        setIsLoaded(false)
      }
    }
  }, [isLoaded])

  useEffect(() => {
    if (player && isLoaded) {
      const vidEl = player.children()[0]
      const attrs = ['playsinline', 'autoplay']
      const setAttributesToPlay = () => {
        Array.prototype.forEach.call(attrs, (attr) => {
          vidEl.setAttribute(attr, attr)
        })
        player.play()
      }
      const setAttributesToPause = () => {
        player.pause()
        Array.prototype.forEach.call(attrs, (attr) => {
          vidEl.removeAttribute(attr)
        })
      }
      if (!controls && !inViewport) {
        setAttributesToPause()
      }
      if (!controls && !inCarousel && inViewport) {
        setAttributesToPlay()
      }
      if (!controls && inCarousel && carouselActive && inViewport) {
        setAttributesToPlay()
        // call to play the video again after 1s if was not loaded earlier
        setTimeout(() => player.play(), 2000)
      }
      if (!controls && inCarousel && !carouselActive) {
        setAttributesToPause()
      }
      if (controls && !inCarousel && !autoplay && !inViewport) {
        player.pause()
      }
    }
  }, [carouselActive, inViewport])

  const videoOpacity = 100

  return (
    <Wrapper
      id={id}
      my={hasSpacing ? [50, null, 150] : 0}
      controls={controls}
      type={videoType}
      opacity={videoOpacity}
      hasError={hasError}
      hasAudience={hasAudiences}
      className={fixAudiences(audiences)}>
      <VisibilitySensor onChange={onChange} partialVisibility>
        {noLazyload ? (
          <ReactPlayerLoader
            manualReloadFromPropChanges={inCarousel}
            onSuccess={handleSuccess}
            onFailure={handleError}
            accountId="640445624001"
            playerId="MsLHNoEO1"
            muted={muted}
            options={{
              fluid: true,
              playsinline: true,
              muted,
              autoplay,
              loop,
            }}
          />
        ) : (
          <LazyLoadContainer once>
            <ReactPlayerLoader
              manualReloadFromPropChanges={inCarousel}
              onSuccess={handleSuccess}
              onFailure={handleError}
              accountId="640445624001"
              playerId="MsLHNoEO1"
              muted={muted}
              options={{
                fluid: true,
                playsinline: true,
                muted,
                autoplay,
                loop,
              }}
            />
          </LazyLoadContainer>
        )}
      </VisibilitySensor>
      {!inCarousel && showPlaceholder && (
        <PlaceholderImg className={placeholderHidden ? 'hidden' : ''}>
          <Image image={image} type={videoType} hideCaption />
        </PlaceholderImg>
      )}
      {!inCarousel && !hasError && (
        <Overlay
          onClick={handleTogglePlay}
          isPlaying={isPlaying}
          type={videoType}>
          <VideoPlayButton replay={isFinished} level="tertiary" />
        </Overlay>
      )}
      {controls && name && (
        <Container pt={theme.space[4]} px={theme.space[4]}>
          <Body10 uppercase>
            {name}
            {duration !== 0 && Number.isInteger(duration) && (
              <>
                <Divider>&#11049;</Divider>
                {formattedTime(duration)}
              </>
            )}
          </Body10>
        </Container>
      )}
    </Wrapper>
  )
}

VideoPlayer.propTypes = {
  id: PropTypes.string,
  audiences: PropTypes.string,
  name: PropTypes.string,
  autoplay: PropTypes.bool,
  carouselActive: PropTypes.bool,
  inCarousel: PropTypes.bool,
  controls: PropTypes.bool,
  image: PropTypes.instanceOf(Object).isRequired,
  loop: PropTypes.bool,
  muted: PropTypes.bool,
  video: PropTypes.instanceOf(Object).isRequired,
  hasSpacing: PropTypes.bool,
  videoMode: PropTypes.string,
  noLazyload: PropTypes.bool,
  villageSlug: PropTypes.string,
  isStandalone: PropTypes.bool,
}

VideoPlayer.defaultProps = {
  id: undefined,
  audiences: '',
  name: '',
  autoplay: false,
  carouselActive: false,
  controls: true,
  loop: false,
  muted: false,
  inCarousel: false,
  hasSpacing: false,
  videoMode: '',
  noLazyload: false,
  villageSlug: '',
  isStandalone: false,
}

export default VideoPlayer
